/**
 * Agile Plan points: the UI shows and edits ROW TOTALS (like the Planner
 * webapp), while Core stores points PER UNIT and the API speaks per unit
 * (decision Q1). This is the one place the two are converted (O3).
 */
import type { PlannerTask } from "@/services/plannerService";

type Qty = number | null | undefined;

/** Round to 2 decimals for display (and the Planner's numeric(8,2)). */
export const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

/** A missing, 0 or negative qty counts as 1 (O12), as the ledger counts it
 *  (`points × (qty or 1)`), so the Pts column and Used always agree. */
export const countedQty = (qty: Qty) => (Number(qty) > 0 ? Number(qty) : 1);

/** Row total → per unit. Exact (never rounded), so it multiplies back to the
 *  total that was typed. */
export function toPerUnit(total: number, qty: Qty): number {
  return (Number(total) || 0) / countedQty(qty);
}

/** Per unit → row total, rounded to 2 decimals so float noise such as
 *  0.1 × 3 never shows. */
export function toTotal(perUnit: number, qty: Qty): number {
  return round2((Number(perUnit) || 0) * countedQty(qty));
}

/** A task's value for a column, as the table shows it: "points" is the row
 *  total, everything else the raw field. Shared by sorting and filtering. */
export function taskFieldValue(t: PlannerTask, key: string): unknown {
  if (key === "points") return toTotal(t.points, t.qty);
  return (t as unknown as Record<string, unknown>)[key];
}
